import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Keeps the Virtual Try-On button's target barcode in step with the selected
 * variant. The button lives inside the media-gallery block, which is outside
 * `<variant-picker>`'s default morph target, so without this it would keep
 * whatever barcode the page first rendered with -- always trying on the
 * originally-featured variant regardless of later swatch clicks.
 *
 * Mirrors solstice-delivery-promise.js: listen on the closest section for
 * productSelect, then pull the freshly rendered node out of the response
 * document and morph it in, so the barcode always comes from the same Liquid
 * rendering the rest of the page uses (no separate client-side barcode logic
 * to keep in sync).
 */
class SolsticeVtoButton extends Component {
  #section;
  #generation = 0;
  #open = (event) => {
    const button = event.target.closest('button[data-barcode]');
    if (button) this.#section?.querySelector('vto-widget')?.openVto(button.dataset.barcode, button);
  };
  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener('click', this.#open);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.removeEventListener('click', this.#open);
    this.#generation++;
  }

  /**
   * Handles the product select event by re-rendering the button.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const generation = ++this.#generation;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html || !this.isConnected || generation !== this.#generation) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const next = html.querySelector('solstice-vto-button');

        // The wrapper persists; an ineligible variant has no button children.
        if (!next) return;

        morph(this, next, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[solstice-vto-button] Event promise rejected:', error);
        }
      });
  };
}

if (!customElements.get('solstice-vto-button')) {
  customElements.define('solstice-vto-button', SolsticeVtoButton);
}
